import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SiteActions, SiteState } from '@next/core-lib/site';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { selectOrganicMatterTableReport } from 'src/app/advice/store/selectors';
import { OrganicMatterTable, ReportType } from 'src/app/shared/models';
import { GetOrganicMatterReport } from 'src/app/store/actions';
import { getCropYear, getReportSort, selectLoggedInEmployeeAsArray } from 'src/app/store/selectors';

import { ReportSortService } from '../services/report-sort.service';

@Component({
  selector: 'app-organic-matter-report',
  templateUrl: './organic-matter-report.component.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class OrganicMatterReportComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  protected readonly router = inject(Router);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  cropYear$ = this.store.pipe(select(getCropYear));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  pageTitle$ = combineLatest([this.cropYear$, this.customer$]).pipe(
    map(
      ([cropYear, customer]) =>
        `${customer?.name ? customer.name : ''} - ${this.locale.translate(
          'organicMatterSupply',
        )} ${cropYear}`,
    ),
  );
  reportType$ = this.store.pipe(select(RouterSelectors.getRouteParam('reportType')));
  isGBM$ = this.reportType$.pipe(
    map((reportType) => reportType === ReportType.GBMWithEnvironmentalImpactPoints),
  );
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));
  cropFieldIds$ = this.store.pipe(select(RouterSelectors.getRouteParam('cropFieldIds')));
  tableData$: Observable<OrganicMatterTable[]> = new Observable();
  sortedTableData$: Observable<OrganicMatterTable[]> = new Observable();

  reportSort$ = this.store.pipe(select(getReportSort));

  buttonItems$ = this.sortService.getButtonItems();

  ngOnInit() {
    this.pageTitle$.pipe(takeUntil(this.destroyed$)).subscribe((pageTitle) => {
      this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
    });

    this.cropFieldIds$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((fieldIdsString) => {
        if (fieldIdsString.length) {
          const fieldIds = JSON.parse(fieldIdsString) as number[];
          this.store.dispatch(new GetOrganicMatterReport(fieldIds));
        } else {
          this.store.dispatch(new GetOrganicMatterReport(null));
        }
        this.tableData$ = this.store.pipe(
          select(selectOrganicMatterTableReport),
          takeUntil(this.destroyed$),
        );
      });

    this.reportSort$.subscribe(
      (reportSort) =>
        (this.sortedTableData$ = this.tableData$.pipe(
          map((tableData) => {
            const totals = tableData.filter((item) => item.metaData.isTotals);
            const sorted = this.sortService.sort({
              elements: tableData.filter((table) => !totals.includes(table)),
              option: reportSort.option,
              descending: reportSort.descending,
              cropFieldNameFunc: (item) => item.metaData.cropFieldName,
              cropNameFunc: (item) => item.metaData.cropName,
            });
            return totals.concat(sorted);
          }),
        )),
    );
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  back() {
    this.router.navigate(['/']);
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }
}
